export const THEME_BUILDER_STORAGE_KEY = 'bwc-theme-builder-colors-v1'

export const THEME_BUILDER_TOKENS = [
  {
    key: 'parchment',
    cssVar: '--color-parchment',
    label: 'App Background',
    role: 'Base',
    defaults: { light: '#eef0f4', dark: '#030303' },
  },
  {
    key: 'canvas',
    cssVar: '--color-canvas',
    label: 'Panel Background',
    role: 'Surface',
    defaults: { light: '#ffffff', dark: '#0a0a0a' },
  },
  {
    key: 'canvas2',
    cssVar: '--color-canvas2',
    label: 'Secondary Panel',
    role: 'Surface',
    defaults: { light: '#e0e3ea', dark: '#161616' },
  },
  {
    key: 'ink',
    cssVar: '--color-ink',
    label: 'Primary Text',
    role: 'Text',
    defaults: { light: '#111318', dark: '#eeeeee' },
  },
  {
    key: 'inkMuted',
    cssVar: '--color-ink-muted',
    label: 'Muted Text',
    role: 'Text',
    defaults: { light: '#3b3f4a', dark: '#a2a2a2' },
  },
  {
    key: 'inkDim',
    cssVar: '--color-ink-dim',
    label: 'Dim Text',
    role: 'Text',
    defaults: { light: '#b0b5c0', dark: '#5c5c5c' },
  },
  {
    key: 'border',
    cssVar: '--color-border',
    label: 'Border',
    role: 'Structure',
    defaults: { light: '#b0b5c0', dark: '#424242' },
  },
  {
    key: 'forest',
    cssVar: '--color-forest',
    label: 'Positive Accent',
    role: 'Accent',
    defaults: { light: '#4a6741', dark: '#6aaa5e' },
  },
  {
    key: 'ember',
    cssVar: '--color-ember',
    label: 'Risk Accent',
    role: 'Accent',
    defaults: { light: '#b83a2e', dark: '#e05a4a' },
  },
]

const TOKEN_BY_KEY = Object.fromEntries(THEME_BUILDER_TOKENS.map(token => [token.key, token]))
const THEME_MODES = new Set(['light', 'dark'])

export function createEmptyThemeBuilderStorage() {
  return {
    light: {},
    dark: {},
    palettes: [],
    selectedPalette: { light: null, dark: null },
  }
}

export function normalizeHex(value) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  return /^#[0-9a-fA-F]{6}$/.test(withHash) ? withHash.toLowerCase() : null
}

export function hexToRgbChannels(value) {
  const normalized = normalizeHex(value)
  if (!normalized) return null
  const intValue = Number.parseInt(normalized.slice(1), 16)
  const r = (intValue >> 16) & 255
  const g = (intValue >> 8) & 255
  const b = intValue & 255
  return `${r} ${g} ${b}`
}

export function getActiveThemeMode() {
  if (typeof window === 'undefined') return 'dark'
  const saved = window.localStorage?.getItem('bwc-dark-mode')
  if (saved !== null) return saved === 'true' ? 'dark' : 'light'
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) return 'dark'
  return 'dark'
}

export function sanitizeThemeColors(values) {
  const clean = {}
  if (!values || typeof values !== 'object') return clean

  for (const [key, rawValue] of Object.entries(values)) {
    if (!TOKEN_BY_KEY[key]) continue
    const normalized = normalizeHex(rawValue)
    if (normalized) clean[key] = normalized
  }

  return clean
}

export function sanitizeThemeBuilderStorage(value) {
  const parsed = value && typeof value === 'object' ? value : {}
  const clean = createEmptyThemeBuilderStorage()

  for (const mode of ['light', 'dark']) {
    clean[mode] = sanitizeThemeColors(parsed?.[mode])
  }

  const seenIds = new Set()
  clean.palettes = Array.isArray(parsed?.palettes)
    ? parsed.palettes.flatMap((rawPalette, index) => {
        const mode = THEME_MODES.has(rawPalette?.mode) ? rawPalette.mode : null
        const name = normalizePaletteName(rawPalette?.name)
        const colors = sanitizeThemeColors(rawPalette?.colors)
        if (!mode || !name || Object.keys(colors).length === 0) return []

        const fallbackId = `theme-${index + 1}`
        const rawId = typeof rawPalette.id === 'string' && rawPalette.id.trim()
          ? rawPalette.id.trim()
          : fallbackId
        const id = seenIds.has(rawId) ? `${rawId}-${index + 1}` : rawId
        seenIds.add(id)

        return [{ id, name, mode, colors }]
      })
    : []

  const selectedPalette = parsed?.selectedPalette || {}
  for (const mode of ['light', 'dark']) {
    const selectedId = typeof selectedPalette?.[mode] === 'string' ? selectedPalette[mode] : null
    clean.selectedPalette[mode] = clean.palettes.some(palette => (
      palette.mode === mode && palette.id === selectedId
    ))
      ? selectedId
      : null
  }

  return clean
}

export function hasThemeBuilderPreferences(storage) {
  const clean = sanitizeThemeBuilderStorage(storage)
  return (
    Object.keys(clean.light).length > 0 ||
    Object.keys(clean.dark).length > 0 ||
    clean.palettes.length > 0 ||
    !!clean.selectedPalette.light ||
    !!clean.selectedPalette.dark
  )
}

function normalizePaletteName(name) {
  if (typeof name !== 'string') return ''
  return name.trim().replace(/\s+/g, ' ').slice(0, 60)
}

function buildFullPaletteColors(mode, values) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const supplied = sanitizeThemeColors(values)

  return Object.fromEntries(
    THEME_BUILDER_TOKENS.map(token => [
      token.key,
      supplied[token.key] || token.defaults[activeMode],
    ])
  )
}

function createPaletteId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `theme-${Math.random().toString(36).slice(2, 10)}`
}

export function readThemeBuilderStorage() {
  if (typeof window === 'undefined') return createEmptyThemeBuilderStorage()

  try {
    const parsed = JSON.parse(window.localStorage.getItem(THEME_BUILDER_STORAGE_KEY) || '{}')
    return sanitizeThemeBuilderStorage(parsed)
  } catch {
    return createEmptyThemeBuilderStorage()
  }
}

export function writeThemeBuilderStorage(nextStorage) {
  const clean = sanitizeThemeBuilderStorage(nextStorage)
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(THEME_BUILDER_STORAGE_KEY, JSON.stringify(clean))
  }
  return clean
}

export function getStoredThemeColors(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  return readThemeBuilderStorage()[activeMode] || {}
}

export function getThemeBuilderValues(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const stored = getStoredThemeColors(activeMode)

  return Object.fromEntries(
    THEME_BUILDER_TOKENS.map(token => [
      token.key,
      stored[token.key] || token.defaults[activeMode],
    ])
  )
}

export function getThemePalettes(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  return readThemeBuilderStorage().palettes.filter(palette => palette.mode === activeMode)
}

export function getSelectedThemePaletteId(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  return readThemeBuilderStorage().selectedPalette[activeMode] || null
}

export function applyThemeBuilderColors(mode = getActiveThemeMode()) {
  if (typeof document === 'undefined') return

  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const stored = getStoredThemeColors(activeMode)

  for (const token of THEME_BUILDER_TOKENS) {
    document.documentElement.style.removeProperty(token.cssVar)
  }

  for (const [key, value] of Object.entries(stored)) {
    const token = TOKEN_BY_KEY[key]
    const rgbChannels = hexToRgbChannels(value)
    if (token && rgbChannels) {
      document.documentElement.style.setProperty(token.cssVar, rgbChannels)
    }
  }
}

export function setThemeBuilderColor(mode, tokenKey, value) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const token = TOKEN_BY_KEY[tokenKey]
  const normalized = normalizeHex(value)
  if (!token || !normalized) return getThemeBuilderValues(activeMode)

  const storage = readThemeBuilderStorage()
  storage[activeMode] = {
    ...storage[activeMode],
    [tokenKey]: normalized,
  }
  storage.selectedPalette[activeMode] = null
  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return getThemeBuilderValues(activeMode)
}

export function resetThemeBuilderMode(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const storage = readThemeBuilderStorage()
  storage[activeMode] = {}
  storage.selectedPalette[activeMode] = null
  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return getThemeBuilderValues(activeMode)
}

export function saveThemePalette(mode, name, colors, { paletteId = null } = {}) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const cleanName = normalizePaletteName(name)
  if (!cleanName) return { error: 'Theme name required' }

  const storage = readThemeBuilderStorage()
  const explicitPalette = paletteId
    ? storage.palettes.find(palette => palette.mode === activeMode && palette.id === paletteId)
    : null
  const selectedId = storage.selectedPalette[activeMode]
  const selectedPalette = selectedId
    ? storage.palettes.find(palette => palette.mode === activeMode && palette.id === selectedId)
    : null
  const sameNamePalette = storage.palettes.find(palette => (
    palette.mode === activeMode &&
    palette.name.toLowerCase() === cleanName.toLowerCase()
  ))
  const targetPalette = explicitPalette || selectedPalette || sameNamePalette
  const palette = {
    id: targetPalette?.id || createPaletteId(),
    name: cleanName,
    mode: activeMode,
    colors: buildFullPaletteColors(activeMode, colors),
  }

  storage.palettes = targetPalette
    ? storage.palettes.map(existing => existing.id === targetPalette.id ? palette : existing)
    : [...storage.palettes, palette]
  storage[activeMode] = palette.colors
  storage.selectedPalette[activeMode] = palette.id

  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return {
    palette,
    palettes: getThemePalettes(activeMode),
    selectedPaletteId: palette.id,
    values: getThemeBuilderValues(activeMode),
  }
}

export function loadThemePalette(mode, paletteId) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const storage = readThemeBuilderStorage()
  const palette = storage.palettes.find(existing => (
    existing.mode === activeMode && existing.id === paletteId
  ))
  if (!palette) return { error: 'Theme not found' }

  storage[activeMode] = buildFullPaletteColors(activeMode, palette.colors)
  storage.selectedPalette[activeMode] = palette.id

  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return {
    palette,
    palettes: getThemePalettes(activeMode),
    selectedPaletteId: palette.id,
    values: getThemeBuilderValues(activeMode),
  }
}

export function clearSelectedThemePalette(mode = getActiveThemeMode()) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const storage = readThemeBuilderStorage()
  storage.selectedPalette[activeMode] = null
  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return {
    palettes: getThemePalettes(activeMode),
    selectedPaletteId: null,
    values: getThemeBuilderValues(activeMode),
  }
}

export function deleteThemePalette(mode, paletteId) {
  const activeMode = THEME_MODES.has(mode) ? mode : getActiveThemeMode()
  const storage = readThemeBuilderStorage()
  const beforeCount = storage.palettes.length
  storage.palettes = storage.palettes.filter(palette => !(
    palette.mode === activeMode && palette.id === paletteId
  ))
  if (storage.palettes.length === beforeCount) return { error: 'Theme not found' }
  if (storage.selectedPalette[activeMode] === paletteId) {
    storage.selectedPalette[activeMode] = null
  }

  writeThemeBuilderStorage(storage)
  applyThemeBuilderColors(activeMode)
  return {
    palettes: getThemePalettes(activeMode),
    selectedPaletteId: getSelectedThemePaletteId(activeMode),
    values: getThemeBuilderValues(activeMode),
  }
}
